/* Servicio de las Microencuestas (nuevos ingresos y Clima Laboral).
 * Centraliza las llamadas al backend (prefijo /microencuesta/...).
 * El token de autenticación lo agrega el interceptor de ./api.
 */
import api from "./api";

export type MicroEncuestaTipoPregunta =
  /** 1..5 de acuerdo. */
  | "LIKERT"
  /** 5 "Sí, definitivamente" .. 1 "No". */
  | "RECOMENDACION"
  /** Opción única de `opciones`; si la opción requiere_texto se pide un texto. */
  | "OPCION"
  /** 0..10 (probabilidad de recomendar). */
  | "NPS"
  /** Respuesta abierta (texto). */
  | "TEXTO";

export interface MicroEncuestaOpcion {
  valor: number;
  texto: string;
  requiere_texto: boolean;
}

export interface MicroEncuestaPregunta {
  id: number;
  orden: number;
  eje: string | null;
  tipo: MicroEncuestaTipoPregunta;
  texto: string;
  obligatoria: boolean;
  opciones: MicroEncuestaOpcion[];
}

export interface MicroEncuestaMomento {
  id: number;
  codigo: string;
  nombre: string;
}

export interface MicroEncuestaPostergacion {
  /** null = "Más tarde" solo cierra el modal (sin límite ni registro). */
  maximo: number | null;
  permitida: boolean;
  restantes: number;
}

export interface MicroEncuesta {
  id: number;
  codigo: string;
  titulo: string;
  subtitulo: string | null;
  pregunta_comentario: string | null;
  texto_confidencialidad: string | null;
  fecha_ingreso: string | null;
  momento: MicroEncuestaMomento;
  postergacion?: MicroEncuestaPostergacion;
  preguntas: MicroEncuestaPregunta[];
}

export interface MicroEncuestaRespuesta {
  pregunta_id: number;
  valor?: number;
  texto?: string;
}

export interface MicroEncuestaEstado {
  /** La app abre el modal sola al iniciar sesión. */
  abrir: boolean;
  /** Encuesta sin responder (tarjeta del inicio); null si no hay ninguna. */
  encuesta: MicroEncuesta | null;
}

/* Eventos de window entre la tarjeta del inicio y el modal (montado en App). */
/** La tarjeta pide abrir el modal; detail = MicroEncuesta. */
export const EVENTO_MICROENCUESTA_ABRIR = "microencuesta-abrir";
/** El modal guardó la encuesta: la tarjeta se oculta. */
export const EVENTO_MICROENCUESTA_RESPONDIDA = "microencuesta-respondida";

const MicroEncuestaAPI = {
  /** Encuesta sin responder del usuario y si corresponde abrirla sola. */
  async estado(): Promise<MicroEncuestaEstado> {
    try {
      const { data } = await api.get(`/microencuesta/pendiente`);
      const encuesta = data?.recordatorio || data?.pendiente ? (data.encuesta as MicroEncuesta) : null;
      return { abrir: !!data?.pendiente && !!encuesta, encuesta };
    } catch {
      // Ante error no bloqueamos el inicio de la app.
      return { abrir: false, encuesta: null };
    }
  },

  async enviar(encuestaId: number, respuestas: MicroEncuestaRespuesta[], comentario?: string) {
    const { data } = await api.post(`/microencuesta/${encuestaId}/respuestas`, {
      respuestas,
      comentario: comentario || null,
    });
    return data;
  },

  /** Registra un "Responder más tarde" (solo campañas con postergacion.maximo). */
  async postergar(encuestaId: number): Promise<{ info: string; restantes: number }> {
    const { data } = await api.post(`/microencuesta/${encuestaId}/postergar`);
    return data;
  },
};

export default MicroEncuestaAPI;
